/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './IOSSwitch';

const IOSSwitchManager: ComponentManagerFn<'IOSSwitch'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'IOSSwitch'>(platform, 'IOSSwitch'),
    type: 'IOSSwitch',
    name: 'IOS Switch',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => {
          return ['YN', 'Boolean', 'TF', 'String'];
        },
      },
      checkedValue: {
        type: 'string',
        label: 'Checked Value',
        maxLength: 1,
      },
      unCheckedValue: {
        type: 'string',
        label: 'Unchecked Value',
        maxLength: 1,
      },
      checkedLabel: {
        type: 'string',
        label: 'Checked Label',
      },
      unCheckedLabel: {
        type: 'string',
        label: 'Unchecked Label',
      },
      label: {
        label: 'Label',
        type: 'expression',
        returnType: async () => 'string',
      },
      size: {
        label: 'Size',
        type: 'number',
        min: 8,
        max: 48,
        step: 4,
      },
      switchWidth: {
        label: 'Min Width',
        type: 'number',
        min: 50,
        max: 120,
        step: 8,
      },
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
        helpText:
          'Specify an expression which evaluate to true to disable this checkbox',
      },
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Toolbar',
    devStyles: { display: 'inline-flex' },
    icon: { iconName: 'check-square' },
    replaceableWith: ['TextField', 'Checkbox', 'RadioBox'],
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    isPropertyVisible: (property) => {
      switch (property) {
        case 'label':
        case 'helperText':
        case 'required':
        case 'flex':
        case 'colorExpression':
        case 'bgColorExpression':
          return false;
        default:
          return true;
      }
    },
    description: `**Overview**<br/>
The IOS Switch is a toggle component styled to resemble the standard toggle switch found in iOS interfaces. It provides a user-friendly way to switch between two mutually exclusive states (On/Off, Yes/No).
Use this component as a modern, touch-friendly alternative to a standard checkbox, especially for settings or status toggles.

**1. Data Connection**<br/>
* **View Attribute:** The component binds to a Data Store field to read and write its value. It supports Boolean (True/False), TF (True/False flags), YN (Yes/No flags), and String (Custom values like "A/B").

**2. Value Configuration**<br/>
You can explicitly define what data is saved to the database for each state.
* **Checked Value:** The value to save when the switch is ON (e.g., "Y" or "Active").
* **Unchecked Value:** The value to save when the switch is OFF (e.g., "N" or "Inactive").

**3. Visual Customization**<br/>
* **Checked Label:** Text displayed inside/next to the switch when ON (e.g., "ON").
* **Unchecked Label:** Text displayed when OFF (e.g., "OFF").
* **Label:** A general label for the field, though its visibility might be suppressed in favor of the internal labels.
* **Size:** Controls the overall scale (8px - 48px).
* **Min Width:** Sets the width of the switch track (50px - 120px) to accommodate longer internal labels.

**4. Interactivity**<br/>
* **TriggerOnChange:** Supports adding a TriggerOnChange child. This allows you to execute logic immediately when the user toggles the switch (e.g., enabling dark mode instantly) without waiting for a save button.
* **Disable Condition:** An expression to programmatically disable the switch (making it read-only) based on logic.

**5. Best Use Cases**<br/>
* **Settings:** "Enable Notifications," "Dark Mode," "Public Profile."
* **Status Toggles:** "Active/Inactive," "Published/Draft."
* **Binary Choices:** "Yes/No," "Male/Female," "Metric/Imperial."`,
  };
};

export default IOSSwitchManager;
